import React from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  useExercises,
  useExerciseOperations,
  useExerciseSearch,
} from "../../../hooks/useExercises";
import { useSearchableList } from "../../../hooks/useSearchableList";
import { useCrudModals } from "../../../hooks/useCrudModals";
import {
  Exercise,
  CreateExerciseInput,
  UpdateExerciseInput,
} from "../../../types/exercise";
import ExercisesTable from "../../../components/exercises/ExercisesTable";
import ExerciseModal from "../../../components/exercises/ExerciseModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import Pagination from "../../../components/common/Pagination";
import ListPageShell from "../../../components/common/ListPageShell";

const LIMIT = 15;

const toExerciseInput = (
  data: any
): CreateExerciseInput & UpdateExerciseInput => ({
  name: data.name,
  is_at_home: data.is_at_home,
  is_gym_based: data.is_gym_based,
  training_method: data.training_method,
  type: data.type,
  yt_link: data.yt_link,
  target_muscle_group: data.target_muscle_group,
  description: data.description,
  admin_note: data.admin_note,
});

const ExerciseLibrary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const modals = useCrudModals<Exercise>();

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialSearchTerm = searchParams.get("search") || "";

  const { exercises, ...exerciseList } = useExercises(initialPage, LIMIT);
  const exerciseSearch = useExerciseSearch();

  const list = useSearchableList<Exercise>({
    list: { items: exercises, ...exerciseList },
    search: exerciseSearch,
    initialSearchTerm,
    initialPage,
  });

  const {
    loading: operationLoading,
    createExercise,
    updateExercise,
    deleteExercise,
    error: operationError,
  } = useExerciseOperations();

  const handleViewExercise = (exercise: Exercise) => {
    navigate(`/admin/exercise-library/${exercise.id}`, {
      state: { from: location.search },
    });
  };

  const confirmDeleteExercise = async () => {
    if (!modals.toDelete) return;

    const success = await deleteExercise(modals.toDelete.id);
    if (success) {
      list.refreshAll();
      modals.closeDelete();
    }
  };

  const handleCreateSubmit = async (exerciseData: any) => {
    const newExercise = await createExercise(toExerciseInput(exerciseData));
    if (newExercise) {
      list.refreshAll();
      modals.closeCreate();
    }
  };

  const handleUpdateSubmit = async (exerciseData: any) => {
    if (!modals.selected) return;

    const updatedExercise = await updateExercise(
      modals.selected.id,
      toExerciseInput(exerciseData)
    );
    if (updatedExercise) {
      list.refreshAll();
      modals.closeEdit();
    }
  };

  return (
    <>
      <ListPageShell
        entityName="exercises"
        onRefresh={list.handleRefresh}
        refreshDisabled={list.listLoading}
        onAdd={modals.openCreate}
        addLabel="Add Exercise"
        error={list.error}
        errorContext={list.hasSearched ? "searching" : "loading"}
        operationError={operationError}
      >
        <ExercisesTable
          exercises={list.items}
          loading={list.loading || operationLoading}
          onEdit={modals.openEdit}
          onDelete={modals.openDelete}
          onView={handleViewExercise}
          onSearch={list.handleSearch}
          onClearSearch={list.handleClearSearch}
          searchTerm={list.lastSearchTerm}
          isSearchMode={list.hasSearched}
        />

        <Pagination
          page={list.page}
          limit={LIMIT}
          total={list.total}
          totalPages={list.totalPages}
          entityName="exercises"
          isSearchMode={list.hasSearched}
          onPageChange={list.onPageChange}
          loading={list.loading}
        />
      </ListPageShell>

      <ExerciseModal
        isOpen={modals.showCreate}
        onClose={modals.closeCreate}
        onSubmit={handleCreateSubmit}
        mode="create"
        loading={operationLoading}
        error={operationError}
      />

      <ExerciseModal
        isOpen={modals.showEdit}
        onClose={modals.closeEdit}
        onSubmit={handleUpdateSubmit}
        exercise={modals.selected}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={modals.showDelete}
        onClose={modals.closeDelete}
        onConfirm={confirmDeleteExercise}
        title="Delete Exercise"
        message="Are you sure you want to delete this exercise?"
        itemName={modals.toDelete?.name || "Untitled Exercise"}
        loading={operationLoading}
        error={operationError}
      />
    </>
  );
};

export default ExerciseLibrary;
